import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts, useWishlist, useBrands } from '../hooks/useProducts';
import ProductGrid from '../components/product/ProductGrid';
import { useAuthStore } from '../stores/authStore';

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [page, setPage] = useState(1);
  const isRTL = i18n.dir() === 'rtl';

  const [selectedBrand, setSelectedBrand] = useState('');
  const { data: brands } = useBrands();

  const hotProduct = searchParams.get('hotProduct') === 'true';
  const dailyDeal = searchParams.get('dailyDeal') === 'true';

  const filters: Record<string, string | number | undefined> = { page, limit: 20 };
  if (hotProduct) filters.hotProduct = 'true';
  if (dailyDeal) filters.dailyDeal = 'true';
  if (selectedBrand) filters.brand = selectedBrand;

  const { data, isLoading } = useProducts(filters);
  const { data: wishlist } = useWishlist(isAuthenticated);

  const wishlistIds = wishlist?.map((item) => item.id) || [];

  // Determine page title and icon
  let pageTitle = isRTL ? 'כל המוצרים' : 'All Products';
  let pageIcon = '🛍️';

  if (hotProduct) {
    pageTitle = isRTL ? 'המוצרים החמים' : 'Hot Products';
    pageIcon = '🔥';
  } else if (dailyDeal) {
    pageTitle = isRTL ? 'הדיל היומי' : 'Daily Deals';
    pageIcon = '⚡';
  }

  const products = data?.products || [];
  const pagination = data?.pagination;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-4">
        <Link to="/" className="hover:text-primary">{t('nav.home')}</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{pageTitle}</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-3xl">{pageIcon}</span>
        <h1 className="text-2xl font-bold">{pageTitle}</h1>
        {pagination && (
          <span className="text-gray-500 text-sm">
            ({pagination.total} {isRTL ? 'מוצרים' : 'products'})
          </span>
        )}
      </div>

      {/* Brand Filter */}
      {brands && brands.length > 0 && (
        <div className="mb-6">
          <select
            value={selectedBrand}
            onChange={(e) => { setSelectedBrand(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">{isRTL ? 'כל המותגים' : 'All Brands'}</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>{brand.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      )}

      {/* Products Grid */}
      <ProductGrid
        products={products}
        wishlistIds={isAuthenticated ? wishlistIds : []}
        isLoading={isLoading}
        emptyMessage={t('product.noProducts')}
      />

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t('common.previous')}
          </button>

          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === pagination.totalPages || Math.abs(p - page) <= 2)
            .map((p, idx, arr) => (
              <span key={p}>
                {idx > 0 && arr[idx - 1] !== p - 1 && (
                  <span className="px-2">...</span>
                )}
                <button
                  onClick={() => setPage(p)}
                  className={`px-4 py-2 border rounded-lg ${
                    p === page
                      ? 'bg-primary text-white border-primary'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              </span>
            ))}

          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}
